import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { SupportAgentOutlined, WhatsApp } from "@mui/icons-material";
import { getAllowedHref } from "src/_modules/AiSupportChat/core/linkPolicy";
import { ESCALATION_REASONS } from "src/_modules/AiSupportChat/core/protocol";

const REASONS = Object.values(ESCALATION_REASONS);

/**
 * Under an answer the backend escalated (plan §3.6, `done.escalation`):
 * «فريق الدعم يمكنه مساعدتك في هذا», the reason line, the WhatsApp button and
 * «سيفتح واتساب في نافذة جديدة. لن يتم فتح تذكرة تلقائيًا.»
 *
 * It only offers WhatsApp: no ticket, no "an agent will contact you"
 * (non-negotiable #1). An unknown reason shows no reason line. The URL goes
 * through the answer link policy; a URL it refuses hides the button and the
 * note about it (the title and reason stay).
 *
 * @param {{ escalation: { reason?: string, whatsappUrl?: string } }} props
 */
export default function EscalationCard({ escalation }) {
  const { t, i18n } = useTranslation("aiSupport");
  const { reason, whatsappUrl } = escalation;
  const reasonKey = `escalation.${reason}`;
  const showReason =
    REASONS.includes(reason) && i18n.exists(reasonKey, { ns: "aiSupport" });
  const href = getAllowedHref(whatsappUrl);
  const noteId = useId();

  return (
    <Box
      data-ai-escalation={reason}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <SupportAgentOutlined
          fontSize="small"
          color="primary"
          aria-hidden="true"
          sx={{ mt: 0.25 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            component="p"
            sx={{ fontWeight: 600 }}
          >
            {t("escalation.title")}
          </Typography>
          {showReason && (
            <Typography variant="body2" color="text.secondary">
              {t(reasonKey)}
            </Typography>
          )}
        </Box>
      </Box>
      {href && (
        <>
          <Button
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="success"
            size="small"
            fullWidth
            startIcon={<WhatsApp />}
            aria-describedby={noteId}
            sx={{ mt: 1.5, textTransform: "none" }}
          >
            {t("escalation.whatsapp")}
          </Button>
          <Typography
            id={noteId}
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mt: 0.75 }}
          >
            {t("escalation.note")}
          </Typography>
        </>
      )}
    </Box>
  );
}
